import { booleanAttribute, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, HostListener, inject, input, model, output, signal, TemplateRef, untracked, viewChild, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { combineLatest, distinctUntilChanged, filter, finalize, forkJoin, map, Observable, of, Subject, Subscription, switchMap, take, tap, timer } from 'rxjs';
import { AppCurrencyService } from 'src/app/app/AppCurrencyService';
import { AppContext } from 'src/app/app/context';
import { DataModelUserApiService, DataQueryService, FilterDto, GetQueryPlanResponse, InsightsUserRole, LocaleDto, QueryPlanDefinition, SchemaType, UserDatasetService, UserRole, UserVisualService, UserVisualStateDto, VisualDto, VisualService } from 'src/app/services/api.services';
import { LocalisationService } from 'src/app/services/localisation.service';
import { getInsightsPbiChartType } from 'src/app/shared/chart/chart';
import { ContentLoadState } from 'src/app/shared/components/content-placeholder/content-placeholder.component';
import { GenericAlertButton, GenericAlertData, GenericAlertDialog, VisualApiExportDialog, VisualApiExportDialogData } from 'src/app/shared/dialogs';
import { catchAppError, TimeZone } from 'src/app/shared/utils';
import { SchemaUtils } from 'src/app/shared/utils/schema-utils';
import { VisualFileExportDialogService } from 'src/app/shared/visual-data-export/visual-data-export.service';
import { isDevEnvironment } from 'src/client-config';
import { environment } from 'src/environments/environment';
import { DataModel, DataRelationshipMappings } from '../dataModel';
import { DataModelResolver } from '../dataModel/DataModelResolver';
import { UserDatasetLoader } from '../userDatasets/UserDatasetLoader';
import { debounced } from '../utils/debounced';
import { ErrorUtils } from '../utils/ErrorUtils';
import { logger } from '../utils/logger';
import { operation } from '../utils/operation';
import { DataQueryVisualDataSource } from '../visuals/data';
import { Visual } from '../visuals/Visual';
import { VisualContentError } from '../visuals/VisualContentError';
import { VisualHost } from '../visuals/VisualHost';
import { VisualHostComponent } from '../visuals/VisualHost.component';
import { CrossFilterGroup, VisualInteractions } from '../visuals/VisualInteractions';
import { FontSize, TextElementStyle, TextStyle } from '../visuals/VisualStyle';
import { DashboardEmbedComponent } from './DashboardEmbed.component';
import { ContextMenuTriggerDirective } from '../../polyfills/context-menu-trigger';
import { FilterUtils } from '../utils/FilterUtils';
import { DialogsService } from '../../services/dialogs.service';
import { validVisualSelectClasses } from 'src/app/pivot-studio/components/visual-editor/VisualEditor.component';

const LOGGER = logger('visual-embed');

@Component({
  selector: 'visual-embed',
  templateUrl: './VisualEmbed.component.html',
  styleUrl: './VisualEmbed.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[class.print]': 'print()',
    '[class.noBorder]': 'noBorder()'
  }
})
export class VisualEmbedComponent {
  readonly document = window.document;

  readonly appContext = inject(AppContext);
  readonly currencyService = inject(AppCurrencyService);
  readonly destroyRef = inject(DestroyRef);
  readonly visualDataSource = inject(DataQueryVisualDataSource);
  readonly dataModelResolver = inject(DataModelResolver);
  readonly dataRelationshipMappings = inject(DataRelationshipMappings);
  readonly localizationService = inject(LocalisationService);

  readonly visualService = inject(VisualService);
  readonly userVisualService = inject(UserVisualService);
  readonly userDatasetService = inject(UserDatasetService);
  readonly dataModelUserApi = inject(DataModelUserApiService);
  readonly dataQueryService = inject(DataQueryService);
  
  readonly dialog = inject(MatDialog);
  readonly visualFileExportDialog = inject(VisualFileExportDialogService);
  readonly userDatasetLoader = inject(UserDatasetLoader);
  readonly dialogs = inject(DialogsService);

  readonly dashboard = inject(DashboardEmbedComponent, {optional: true});

  constructor() {
    this.currencyService.ensureLoaded();
  }

  ngOnDestroy() {
  }

  readonly localeInput = input<string | LocaleDto>(null, { alias: 'locale' });
  readonly locale = computed<LocaleDto>(() => {
    let locale = this.localeInput();
    if (typeof locale === 'string')
      locale = this.appContext.findLocaleById(locale);

    return locale ?? this.appContext.locale();
  });

  readonly currencies = this.currencyService.currencies;
  readonly currenciesLoaded = this.currencyService.loaded;

  readonly interactions = input<VisualInteractions>(null);

  readonly pageHeader = input<TemplateRef<any>>();
  readonly pageFooter = input<TemplateRef<any>>();
  
  readonly print = input<boolean>(false);
  readonly busy = input<boolean>(false);

  readonly noBorder = input<boolean>(false);

  readonly ContentLoadState = ContentLoadState;
  readonly contentState = signal<ContentLoadState>(ContentLoadState.Loading);
  readonly contentStateOutput = output<ContentLoadState>({ alias: 'contentState' });
  readonly contentStateChangedAt = model<Date>(new Date());

  readonly errorDetails = signal<string>(null);
  readonly visualHost = viewChild('visualHost', { read: VisualHostComponent });

  readonly showRowCountInput = input(true, { alias: 'showRowCount' });
  readonly showRowCount = computed(() => this.showRowCountInput() && !!this.visual()?.type?.showRowCount);
  readonly rowCount = computed(() => this.visualHost()?.rowCount());
  
  readonly contentError = signal<VisualContentError>(null);


  readonly firstLoadFinished = signal(false);

  readonly loadStartDate = signal(new Date());
  readonly loadEndDate = signal(new Date());
  readonly loadStartTime = signal(0);
  readonly loadEndTime = signal(0);
 
  readonly loadDurationMs = computed(() => this.loadEndTime() - this.loadStartTime());
  readonly loadDurationMsIntString = computed(() => Math.ceil(this.loadDurationMs()).toFixed(0));


  readonly firstRenderStarted = signal(false);
  readonly firstRenderFinished = signal(false);
  
  readonly renderStartDate = signal(new Date());
  readonly renderEndDate = signal(new Date());
  readonly renderStartTime = signal(0);
  readonly renderEndTime = signal(0);
  
  readonly renderDurationMs = computed(() => this.renderEndTime() - this.renderStartTime());
  readonly renderDurationMsIntString = computed(() => Math.ceil(this.renderDurationMs()).toFixed(0));


  readonly visualIdString = computed(() => this.visualDto()?.id ?? 'Unknown');
  readonly createdByString = computed(() => this.visualDto()?.createdBy ?? 'Unknown');

  readonly queryId = computed(() => this.visualHost()?.component()?.getDataQueryId());
  readonly queryPlanLoaded = signal(false);
  readonly queryPlanLoading = signal(false);

  readonly queryPlanResponse = signal<GetQueryPlanResponse>(null);
  readonly queryPlan = computed(() => this.queryPlanResponse()?.plan);
  readonly queryPlanQueryIdChange = effect(() => {
    if (!this.queryPlanResponse())
      return;

    if (this.queryId() != this.queryPlanResponse()?.queryId) {
      untracked(() => {
        this.queryPlanLoaded.set(false);
        this.queryPlanLoading.set(false);
        this.queryPlanResponse.set(null);
      });
    }
  });

  readonly queryPlanViewTemplate = viewChild<TemplateRef<any>>('planTemplate');

  loadQueryPlanClick() {
    if (!this.queryId())
      return;

    this.queryPlanLoading.set(true);
    this.queryPlanLoaded.set(false);

    this.dataQueryService.getQueryPlan({
      queryId: this.queryId()
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(p => {
        this.queryPlanResponse.set(p);
        this.queryPlanLoaded.set(true);
        this.queryPlanLoading.set(false);
      }),
      catchAppError(err => {
        this.queryPlanLoaded.set(false);
        this.queryPlanLoading.set(false);
      }, { errorMessage: 'Failed to load query plan definition' })
    ).subscribe();
  }

  viewQueryPlanClick() {
    if (!this.queryPlan())
      return;

    this.dialogs.openAlert({
      buttons: [GenericAlertButton.close],
      content: this.queryPlanViewTemplate(),
      showClose: true,
      title: 'Query Plan',
    }, { width: '1200px', maxWidth: '100vw' });
  }

  showDiagnosticDetailsClick(content: TemplateRef<any>) {
    this.dialog.open(GenericAlertDialog, {
      width: '400px',
      data: <GenericAlertData>{
        title: 'Diagnostics',
        titleIcon: 'fas fa-wrench',
        content: content,
        buttons: [GenericAlertButton.ok],
      }
    });
  }


  private resetErrorState() {
    this.errorDetails.set(null);
  }

  showErrorDetailsClick(content: TemplateRef<any>) {
    this.dialog.open(GenericAlertDialog, {
      maxWidth: '1200px',
      maxHeight: '800px',
      data: <GenericAlertData>{
        title: 'Error Details',
        titleIcon: 'fas fa-exclamation-triangle',
        content: content,
        buttons: [GenericAlertButton.ok],
      }
    });
  }


  private markAsRenderedAfterLoadingTimeout: Subscription;

  startMarkAsRenderedAfterLoadingTimeout() {
    this.markAsRenderedAfterLoadingTimeout =
      timer(environment.visualRenderTimeout).pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          if (this.contentState() != ContentLoadState.Loaded)
            return;

          let timeoutInSec = environment.visualRenderTimeout / 1000;
          this.onError(`Visual failed to complete rendering in ${timeoutInSec}s`);
        })
      ).subscribe();
  }

  stopMarkAsRenderedAfterLoadingTimeout() {
    this.markAsRenderedAfterLoadingTimeout?.unsubscribe();
    this.markAsRenderedAfterLoadingTimeout = null;
  }

  private setContentState(contentState: ContentLoadState) {
    untracked(() => {
      if (contentState == this.contentState())
        return;

      LOGGER.debug(`ContentState = ${contentState}`, { embed: this, visual: this.visualDto()?.name });
      
      this.contentState.set(contentState);
      this.contentStateChangedAt.set(new Date());
      this.contentStateOutput.emit(contentState);

      if (contentState != ContentLoadState.Error)
        this.resetErrorState();

      if (contentState == ContentLoadState.Loaded) {
        this.startMarkAsRenderedAfterLoadingTimeout();
      }
      else {
        this.stopMarkAsRenderedAfterLoadingTimeout();

        if ([ContentLoadState.Deleted, ContentLoadState.Error, ContentLoadState.Rendered, ContentLoadState.Unavailable].includes(contentState)) {
          this.emitVisualRendered();
        }
      }
    });
  }

  readonly loading = computed(() => [
    ContentLoadState.Loading,
    ContentLoadState.Refreshing
  ].includes(this.contentState()));

  readonly loadingDetails = computed(() => !this.visualDto() || [
    ContentLoadState.Deleted,
    ContentLoadState.Refreshing,
    ContentLoadState.Unavailable,
  ].includes(this.contentState()));
 
  readonly rendered = computed(() => [ContentLoadState.Rendered].includes(this.contentState()));
  readonly renderedOrError = computed(() =>
    this.rendered() ||
    [ContentLoadState.Deleted, ContentLoadState.Error, ContentLoadState.Unavailable].includes(this.contentState()));

  readonly visualDtoInput = input<VisualDto>(null, { alias: 'visualDto' });
  readonly visualIdInput = input<number>(null, { alias: 'visualId' });

  readonly visualDto = signal<VisualDto>(null);
  readonly visualId = computed<number>(() => this.visualDto()?.id ?? this.visualIdInput());
  readonly userVisualState = signal<UserVisualStateDto>({});
 
  readonly visual = signal<Visual>(null);
  readonly visualOutput = output<Visual>({ alias: 'visual' });

  readonly visualRenderStarted = signal(false);
  readonly visualRenderStartedOutput = output<void>({ alias: 'visualRenderStarted' });

  readonly visualRenderPending = signal(false);
  readonly visualRendering = signal(false);
  
  readonly visualRendered = signal(false);
  readonly visualRenderedOutput = output<void>({ alias: 'visualRendered' });

  private readonly updateVisualRendering = debounced(() => {
    this.visualRendering.set(!this.rendered() || this.visualRenderPending());
  }, 100, this.destroyRef);

  private readonly updateVisualRenderingEffect = effect(() => {
    let _ = [this.rendered(), this.visualRenderPending()];
    untracked(() => this.updateVisualRendering());
  });
  
  private emitVisualRenderStarted() {
    this.visualRenderStarted.set(true);
    this.visualRenderStartedOutput.emit();
  }

  private emitVisualRendered() {
    this.visualRendered.set(true);
    this.visualRenderedOutput.emit();
  }

  private onError(error?: any) {
    if (error instanceof VisualContentError)
      this.contentError.set(error);
    else 
      this.contentError.set(VisualContentError.Generic);

    this.errorDetails.set(ErrorUtils.formatForUser(error));
    this.setContentState(ContentLoadState.Error);
  }


  readonly beforeVisualEvent = output<VisualHost.Event>();
  readonly visualEvent = output<VisualHost.Event>();

  onBeforeVisualEvent(e: VisualHost.Event) {
    this.beforeVisualEvent.emit(e);

    if (e.type == VisualHost.EventType.ClickEarlyEvent && e.action === VisualHost.ClickAction.None && e.resetCrossFilter) {
      this.clearSelection();
    }
  }

  onVisualEvent(e: VisualHost.Event) {
    if (e.type == VisualHost.EventType.Loaded) {
      if (!this.firstLoadFinished()) {
        this.firstLoadFinished.set(true);
        this.loadEndDate.set(e.date);
        this.loadEndTime.set(e.timeMs);
      }

      this.setContentState(ContentLoadState.Loaded);
    }

    if ([VisualHost.EventType.RowCountLoadingEvent, VisualHost.EventType.RenderStart].includes(e.type)) {
      // When data is reloaded/filtered or re-rendering, clear error
      this.contentError.set(null);

      if (this.contentState() == ContentLoadState.Error) {
        // Clear previous error state
        this.setContentState(ContentLoadState.Loaded);
      }
    }

    if (e.type == VisualHost.EventType.RenderStart) {
      this.visualRenderPending.set(true);

      if (!this.firstRenderStarted()) {
        this.firstRenderStarted.set(true);
        this.renderStartDate.set(e.date);
        this.renderStartTime.set(e.timeMs);
      }

      if (!!this.visual()?.type.loadedWhenRendering) {
        this.setContentState(ContentLoadState.Loaded);
      }
      
      this.emitVisualRenderStarted();
    }

    if (e.type == VisualHost.EventType.RenderComplete) {
      this.visualRenderPending.set(false);

      if (!this.firstRenderFinished()) {
        this.firstRenderFinished.set(true);
        this.renderEndDate.set(e.date);
        this.renderEndTime.set(e.timeMs);
      }
    }

    if (e.type == VisualHost.EventType.StateChangeEvent) {
      this.updateUserVisualStateDebouced();
    }

    if (e.type == VisualHost.EventType.ContentErrorChangeEvent) {
      this.contentError.set(e.error);
    }

    this.visualEvent.emit(e);
  }

  onVisualRendered(e: VisualHost.RenderState) {
    if (e.failed) {
      this.onError(e.error);
    }
    else {
      this.setContentState(ContentLoadState.Rendered);
    }
  }

  private clearVisual() {
    if (!this.visual())
      return;

    this.visual.set(null);
    this.visualOutput.emit(null);
  }

  private setVisual() {
    this.firstLoadFinished.set(false);
    this.loadStartDate.set(new Date());
    this.loadStartTime.set(performance.now());
    this.firstRenderStarted.set(false);
    this.firstRenderFinished.set(false);

    let visualDto = this.visualDto();
    let dataModel = this.dataModel();

    let visual = Visual.createFromDto(visualDto, {
      currencies: this.currencies,
      dataModel,
      interactions: this.interactions(),
      enableAllFilters: false,
      visualOptions: {},
      userVisualState: this.userVisualState(),
      locale: this.locale
    });

    this.visual.set(visual);
    this.visualOutput.emit(visual);
  }

  private saveUserVisualState = operation(() => {
    return this.userVisualService.setVisualState(this.visualId(), this.userVisualState()).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchAppError({ errorMessage: `Failed to save visual state for this user.` })
    );
  }, { debounce: 400, destroyRef: this.destroyRef });

  private updateUserVisualState() {
    untracked(() => {
      let state = this.visual()?.state();
      if(!state)
        return;

      let userVisualState = state.copyToUserVisualStateDto(this.userVisualState() ?? {});
      this.userVisualState.set(userVisualState);

      this.saveUserVisualState();
    });
  }
  
  private updateUserVisualStateDebouced = debounced(() => this.updateUserVisualState(), 50, this.destroyRef);


  readonly pageHeight = input<number | string>('100%');
  readonly pageHeightCss = computed(() => {
    let h = this.pageHeight();
    return typeof h === 'number' ? `${h}px` : h;
  });

  readonly dataModel = signal<DataModel>(null);

  readonly title = computed(() => this.visualDto()?.name);
  readonly titleStyle = computed<TextElementStyle>(() => {
    let style = TextElementStyle.parse(this.visualDto()?.content?.style, 'title', {
      enabled: true,
      fontSize: { value: 14, kind: 'px' },
    });

    if (!FontSize.value(style.fontSize)) {
      style = {
        ...style,
        fontSize: {
          value: 14,
          kind: 'px',
        }
      };
    }

    return style;
  });

  readonly titleStyleCss = computed(() => TextStyle.toCssVariables(this.titleStyle()));
  readonly hasTitle = computed(() => this.titleStyle().enabled);

  readonly subtitle = computed(() => this.visualDto()?.content?.subtitle);
  readonly subtitleStyle = computed<TextElementStyle>(() => {
    let style = TextElementStyle.parse(this.visualDto()?.content?.style, 'subtitle', {
      enabled: false,
      fontSize: { value: 12, kind: 'px' },
    });

    if (!FontSize.value(style.fontSize)) {
      let titleStyle = this.titleStyle();

      style = {
        ...style,
        fontSize: {
          value: FontSize.value(titleStyle.fontSize, 14) - 2,
          kind: 'px'
        }
      };
    }

    return style;
  });

  readonly subtitleStyleCss = computed(() => TextStyle.toCssVariables(this.subtitleStyle()));
  readonly hasSubtitle = computed(() => !!this.titleStyle()?.enabled && this.subtitleStyle().enabled);

  /** Is all content for visual to be rendered loaded (e.g. model, user dataset)? */
  readonly visualContentLoaded = signal(false);
  readonly datasetLoaded = signal(true);

  readonly canRender = computed(() => !!this.visual() && this.dataModel() && this.visualContentLoaded() && this.datasetLoaded());

  readonly showPlaceholder = computed(() => {
    if (!this.canRender())
      return true;

    if (this.renderedOrError()) {
      if (this.contentError())
        return true;
    }

    let contentState = this.contentState();
    if (this.visual()?.type.showOverlayUntilRendered) {
      if (this.visualRendering())
        return true;

      return ![ContentLoadState.Rendered].includes(contentState);
    }
    else {
      return ![ContentLoadState.Loaded, ContentLoadState.Rendered].includes(contentState);
    }
  });


  private onVisualDtoLoaded(visualDto: VisualDto): Observable<any> {
    this.visualContentLoaded.set(false);
    this.visualDto.set(visualDto);

    if (SchemaUtils.isUserDataset(visualDto.schema)) {
      this.datasetLoaded.set(false);

      let visualSet = false;

      return combineLatest([
        this.userDatasetLoader.load(visualDto.schema.userDatasetId, { tryLoadModelOnFail: true }),
        this.dataModelResolver.getUserDatasetModel(visualDto.schema.userDatasetId)
      ]).pipe(
        tap(([r, m]) => {
          if (r.dataModel || m) {
            this.dataModel.set(r.dataModel ?? m);
            if (!visualSet) {
              visualSet = true;
              this.setVisual();
            }
          }

          if (!r.done && r.state === ContentLoadState.Refreshing) {
            this.setContentState(ContentLoadState.Refreshing);
          }

          if (r.done) {
            if (r.state == ContentLoadState.Error) {
              this.onError(r.error);
            }
            else {
              this.setContentState(r.state);
            }

            if (r.success) {
              this.datasetLoaded.set(true);
              this.visualContentLoaded.set(true);
            }
          }
        }),
        filter(([r,]) => r.done),
        take(1)
      );
    }
    else {
      this.datasetLoaded.set(true);

      return this.dataModelResolver.getOrganisationModel().pipe(
        take(1),
        tap(m => {
          this.dataModel.set(m);
          this.setVisual();
          this.visualContentLoaded.set(true);
        })
      );
    }
  }

  private currentLocale: LocaleDto;
  private currentTimeZone: TimeZone;

  private readonly loadVisualOnSourceChange = effect(() => {
    this.loadVisual();
  });

  private readonly reloadVisualOnSettingsChange = effect(() => {
    // Reload on locale/tz change
    let [locale, tz] = [this.appContext.locale(), this.appContext.timeZone()];
    if (locale !== this.currentLocale || tz !== this.currentTimeZone) {
      untracked(() => this.loadVisual(true));
    }
  });
  
  private reloadToken = 0;
  private onVisualDtoLoadedSub: Subscription;

  loadVisual(reload?: boolean) {
    let source = this.visualDtoInput() ?? this.visualIdInput();

    untracked(() => {
      this.currentLocale = this.appContext.locale();
      this.currentTimeZone = this.appContext.timeZone();

      this.resetErrorState();

      if (!source) {
        this.setContentState(ContentLoadState.Unavailable);
        return;
      }

      if (reload)
        this.reloadToken++;

      this.nextVisualSource$.next([source, this.reloadToken]);
    });
  }
  
  private readonly nextVisualSource$ = new Subject<[VisualDto | number, number]>();
  private readonly loadNextVisualSource = this.nextVisualSource$.pipe(
    takeUntilDestroyed(this.destroyRef),
    distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
    map(([v]) => v),
    switchMap(v => {
      let from = of(v);

      return this.currencyService.load().pipe(
        switchMap(() => from.pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(() => {
            this.clearVisual();
            this.setContentState(ContentLoadState.Loading);
          }),
          switchMap(v => {
            let [visualId, getVisual] = 
              (typeof v === 'number')
                ? [v, this.visualService.getVisual(v, false)]
                : [v.id, of(v)];

            return forkJoin([getVisual, this.userVisualService.getVisualState(visualId)]);
          }),
          tap(([v, s]) => {
            this.userVisualState.set(s);

            this.onVisualDtoLoadedSub?.unsubscribe();
            this.onVisualDtoLoadedSub = this.onVisualDtoLoaded(v).pipe(
              catchAppError(err => this.onError(err), { showErrorSnack: false }),
              takeUntilDestroyed(this.destroyRef)
            ).subscribe();
          }),
          catchAppError(err => this.onError(err), { showErrorSnack: false }),
        ))
      );
    })
  ).subscribe();



  readonly checkInputs = effect(() => {
    let visualDto = this.visualDtoInput();
    let visualId = this.visualIdInput();

    if ([visualDto, visualId].filter(x => !!x).length > 1) {
      LOGGER.warn(`Only one of [visualDto] or [visualId] inputs should be provided, not both.`);
    }
  });

  readonly disableTitle = input(false, { transform: booleanAttribute });
  readonly disableSubtitle = computed(() => this.disableTitle());
  readonly disableActions = input(false, { transform: booleanAttribute });
  readonly disableInteraction = input(false, { transform: booleanAttribute });
  readonly hideTopbar = computed(() => this.disableTitle() && this.disableActions() && this.disableInteraction());

  readonly allowApiDownloadLinkDialog = input(true);
  readonly allowExport = input(true);
  readonly allowReload = input(true);
  readonly allowDiagnostics = computed(() => this.appContext.user()?.role >= UserRole.InsightsAdmin);
  readonly allowEdit =  computed(() => this.appContext.user()?.role >= UserRole.InsightsAdmin);

  readonly showFocusToggle = input(false);
  readonly focused = model(false);


  openApiDownloadLinkDialogClick() {
    if (!this.allowApiDownloadLinkDialog())
      return;

    let visualDto = this.visualDto();
    let visual = this.visual();

    this.dialog.open(VisualApiExportDialog, {
      ...VisualApiExportDialog.defaultOptions,
      data: <VisualApiExportDialogData>{
        visualId: this.visualId(),
        showOpenLinks: this.appContext.user().role >= InsightsUserRole.SuperAdmin && isDevEnvironment(),
        metadata: {
          name: visualDto.name,
          dataset: visual.context.schema.title,
          isExtendedDataset: visualDto.schema.type == SchemaType.UserDataset,
          type: getInsightsPbiChartType(visualDto.content.type).title,
          currency: visualDto.content?.currency ?? visualDto.preferences?.currency
        },
        showCurrency: visual.context.schema.supportsCurrencyConversion,
      }
    });
  }

  exportClick() {
    if (!this.allowExport())
      return;

    this.visualFileExportDialog.openDialog({
      visualId: this.visualId(),
      visual: this.visualDto(),
      filters: [
        ...(this.visualDto()?.content?.filters ?? []),
        ...this.resolveFilterDtosForDashboardsVisual()
      ]
    }, this.visualDto().name);
  }

  resolveFilterDtosForDashboardsVisual(): FilterDto[] {
    let visualComponent = this.visualHost().component();

    let filters = visualComponent.interactions?.requestFilters?.bySchema?.get(visualComponent.schema);

    if (!filters || !filters().length)
      return [];

    return filters().map(x => x.createFilterDto());
  }

  focusToggleClick() {
    this.focused.update(x => !x);
  }

  reloadClick() {
    this.loadVisual(true);
  }


  readonly hasSelection = computed(() => !!this.visualHost()?.hasSelection());

  clearSelectionClick() {
    this.clearSelection();
  }

  clearSelection() {
    let host = this.visualHost();
    host?.selectionClear(VisualHost.EventTrigger.External);
  }


  onBecomeCrossFilterSource() {
    let host = this.visualHost();
    host?.onBecomeCrossFilterSource(VisualHost.EventTrigger.External);
  }

  onBecomeCrossFilterTarget() {
    let host = this.visualHost();
    host?.onBecomeCrossFilterTarget(VisualHost.EventTrigger.External);
  }



  readonly actionMenuTriggers = viewChildren(MatMenuTrigger);
  readonly contextMenuTriggerDirective = viewChildren(ContextMenuTriggerDirective);

  private _actionMenuOpening = false;
  readonly actionMenuOpened = signal(false);

  onActionMenuOpened() {
    this._actionMenuOpening = true;
    this.actionMenuOpened.set(true);

    timer(500).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => this._actionMenuOpening = false)
    ).subscribe();
  }

  onActionMenuClosed() {
    this.actionMenuOpened.set(false);
  }


  @HostListener('document:click', ['$event'])
  @HostListener('document:contextmenu', ['$event'])
  onDocumentClick(e: MouseEvent) {
    if (this._actionMenuOpening)
      return;

    this.actionMenuTriggers()?.forEach(x => x.closeMenu());
    this.contextMenuTriggerDirective()?.forEach(x => x.closeMenu());

    if (e.button === 0 && !this.actionMenuOpened()) {
      let target = e.target as HTMLElement;

      if (![...validVisualSelectClasses, '.embed-visual__content', '.embed-visual__actions'].some(x => target.closest(x))) {
        this.clearSelection();
      }
    }
  }

  readonly editUrl = computed(() => `/visuals/${this.visualId()}`);


  readonly CrossFilterState = VisualEmbed.CrossFilterState;
  readonly crossFilterState = computed(() => {
    let [interactions, host] = [this.interactions(), this.visualHost()]

    if (!interactions || !host)
      return VisualEmbed.CrossFilterState.None;

    if (interactions.crossFilter.source() === host)
      return VisualEmbed.CrossFilterState.Source;

    if (host.component()?.appliedCrossFilterGroups()?.length) 
      return VisualEmbed.CrossFilterState.Target;

    return VisualEmbed.CrossFilterState.None;
  });


  readonly contextMenuTriggerEventFilter = (e: MouseEvent) => {
    const ignoredParents = [
      '.embed-visual__content',
      '.embed-visual__title',
    ];

    if (ignoredParents.some(x => (e.target as HTMLElement).closest(x))) {
      return false;
    }

    return true;
  }

  readonly showCrossFilterButton = computed(() =>
    (this.dashboard && this.appContext.user()?.hasRole(this.appContext.features()?.uI2.crossFilterVisualIcon)
    && this.crossFilterState() >= VisualEmbed.CrossFilterState.Target)
  );

  readonly appliedCrossFilterGroups = computed(() => {
    let visualComponent = this.visualHost()?.component();
    if (!visualComponent)
      return [];

    let fieldResolver = visualComponent.context.visualFieldTargetResolver();
    let dataFormatter = visualComponent.context.dataFormatter();
    let filterFormatter = new FilterUtils.Formatter(fieldResolver, dataFormatter);

    let groups: AppliedCrossFilterGroup[] = visualComponent.appliedCrossFilterGroups().map(x => ({
      filters: x.filters.map(f => ({
        description: filterFormatter.formatDescription(f.createFilterDto(), {
          capitalizeFirstWord: true,
          longFieldTitles: false,
        })
      }))
    }));

    return groups;
  });

  readonly noCrossFilterApplied = computed(() =>
    !this.appliedCrossFilterGroups()?.some(x => !!x.filters?.length));
}


interface AppliedCrossFilterGroup {
  readonly filters: ReadonlyArray<{ description: string; }>;
}

export namespace VisualEmbed {
  export enum CrossFilterState {
    /** Does not participate in current cross filtering. */
    None,

    /** Source of cross filtering data points. */
    Source,

    /** Target of cross filtering. */
    Target,
  }
}
